/**
 * EmbedHub — public info page documenting how to embed Bodenblick widgets
 * on external websites (schools, imkers, ag-blogs).
 *
 * Route: /embed (public, no auth)
 *
 * Each widget gets a live preview iframe + copy-paste HTML snippet.
 */
import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Copy, Check, Code, ArrowLeft, Leaf, Heart } from "@phosphor-icons/react";

const ORIGIN = process.env.REACT_APP_BACKEND_URL || "https://bodenblick.com";

const WIDGETS = [
  {
    id: "pulse",
    title: "Bodenblick Pulse",
    description:
      "Drei Live-Zahlen auf einen Blick: aktueller Stress-Hotspot (NDVI), Brände in Europa und Bienenflug-Status. Aktualisiert sich alle 5 Minuten.",
    url: "/widget/pulse",
    height: 380,
    color: "#00FA9A",
  },
  {
    id: "wildfires",
    title: "Waldbrand-Monitor",
    description:
      "NASA-FIRMS-Hot-Spot-Zählung für Welt, Europa und DE/AT in den letzten 24 Stunden. Ideal für Imker, Förster, Wandergruppen.",
    url: "/widget/wildfires",
    height: 260,
    color: "#FF6A5A",
  },
  {
    id: "heute",
    title: "Beitrag des Tages",
    description:
      "Der aktuellste „Heute\"-Beitrag mit Bild, Titel und Anriss. Verlinkt direkt auf bodenblick.com. Sehr leicht — kein Pflichtfeld.",
    url: "/widget/heute",
    height: 460,
    color: "#D9A87C",
  },
  {
    id: "fructan",
    title: "Fruktan-Warner (Pferde)",
    description:
      "Hufrehe-Risiko anhand Bodenfeuchte und Sonnenscheindauer für eine wählbare Region. Pflicht-Lektüre für Pferdehalter.",
    url: "/widget/fructan/at-pannonisches-flachland",
    height: 320,
    color: "#FFC24B",
  },
];

function snippet(url, height) {
  return (
`<iframe
  src="${ORIGIN}${url}"
  width="360"
  height="${height}"
  style="border:1px solid #1A1A1A;max-width:100%"
  loading="lazy"
  title="Bodenblick-Widget"
></iframe>`
  );
}

function CopyBlock({ code, testid }) {
  const [done, setDone] = useState(false);
  const handle = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setDone(true);
      setTimeout(() => setDone(false), 1800);
    } catch {
      /* fallback handled silently */
    }
  };
  return (
    <div className="relative">
      <pre className="bg-[#0F0F0F] text-[#9FE8C4] border border-white/10 text-[12px] leading-relaxed p-4 pr-12 overflow-x-auto font-mono whitespace-pre">
        <code>{code}</code>
      </pre>
      <button
        onClick={handle}
        className="absolute top-3 right-3 px-2 py-1 text-[11px] bg-[#0A0A0A] text-white font-mono rounded-sm hover:bg-[#121212] inline-flex items-center gap-1"
        data-testid={testid}
      >
        {done ? <><Check size={12} /> kopiert</> : <><Copy size={12} /> Kopieren</>}
      </button>
    </div>
  );
}

export default function EmbedHub() {
  return (
    <>
      <Helmet>
        <title>Widgets einbetten · Bodenblick</title>
        <meta
          name="description"
          content="Bette Bodenblick-Live-Daten auf deiner Website ein: Pulse, Waldbrand-Monitor, Beitrag des Tages und Fruktan-Warner. Kostenlos, DSGVO-konform, ohne Cookies."
        />
      </Helmet>
      <div className="min-h-screen bg-[#0A0A0A]" data-testid="embed-hub">
        <header className="border-b border-white/15 bg-[#121212]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-[#00FA9A] hover:underline" data-testid="embed-back-home">
              <ArrowLeft size={14} /> <Leaf size={14} weight="fill" /> Bodenblick
            </Link>
            <Link to="/heute" className="text-xs text-white/60 hover:text-[#00FA9A]">→ Heute lesen</Link>
          </div>
        </header>

        <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
          <div className="mb-12 max-w-3xl">
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#00FA9A] font-mono mb-3 inline-flex items-center gap-2">
              <Code size={12} /> Widgets · Embeds · Iframes
            </p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-[5rem] font-bold text-white tracking-tighter leading-[0.95] bb-accent-stripe">
              Bringe Bodenblick auf deine Website.
            </h1>
            <p className="mt-5 text-base text-white/70 leading-relaxed">
              Vier kleine, DSGVO-konforme Widgets — kein Tracking, keine Cookies,
              keine Anmeldung. Einfach das HTML kopieren und bei dir einfügen.
              Schulen, Imker-Vereine, Naturparks und Vereine dürfen die Widgets
              frei nutzen.
            </p>
            <p className="mt-3 text-sm text-white/60 inline-flex items-center gap-1.5">
              <Heart size={12} weight="fill" className="text-[#FF7A45]" />
              Kostenlos für nicht-kommerzielle Nutzung. Bei kommerzieller Nutzung
              bitte kurz Bescheid geben.
            </p>
          </div>

          <div className="space-y-16">
            {WIDGETS.map((w, idx) => (
              <section
                key={w.id}
                className="grid lg:grid-cols-[1fr_1.2fr] gap-8 items-start"
                data-testid={`embed-widget-${w.id}`}
              >
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-mono" style={{ color: w.color }}>
                    Widget · {String(idx + 1).padStart(2, "0")}
                  </div>
                  <h2 className="text-2xl font-bold text-white mt-2 mb-3">
                    {w.title}
                  </h2>
                  <p className="text-sm text-white/75 leading-relaxed mb-4">
                    {w.description}
                  </p>
                  <CopyBlock code={snippet(w.url, w.height)} testid={`copy-${w.id}`} />
                  <div className="mt-3 text-[11px] text-white/50 font-mono">
                    Vorschau live · {w.height} px hoch · ladbar
                  </div>
                </div>
                <div className="border border-white/15 bg-[#121212] p-3">
                  <iframe
                    src={`${ORIGIN}${w.url}`}
                    width="360"
                    height={w.height}
                    style={{ border: 0, maxWidth: "100%", display: "block", margin: "0 auto" }}
                    loading="lazy"
                    title={w.title}
                    data-testid={`iframe-${w.id}`}
                  />
                </div>
              </section>
            ))}
          </div>

          <div className="mt-20 border-t border-white/15 pt-8 text-xs text-white/60 leading-relaxed">
            <p>
              Alle Widgets sind reine Iframes auf bodenblick.com — sie setzen
              keine Cookies und übertragen keine personenbezogenen Daten an
              Drittanbieter. Wir empfehlen dennoch, sie in deiner Datenschutz-
              erklärung zu erwähnen.
            </p>
            <p className="mt-2">
              Probleme oder Feature-Wünsche? <a className="text-[#00FA9A] hover:underline" href="https://bodenblick.com/impressum">Kontakt</a>.
            </p>
          </div>
        </main>
      </div>
    </>
  );
}
